import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faThLarge, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { ResponsiveContainer, Treemap, Tooltip } from 'recharts';
import { accountApi } from '../api/accountApi';
import { schwabApi } from '../api/schwabApi';

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

/** Map P/L % to a green/red fill (neutral near 0). */
function plColor(pct, maxAbs = 8) {
  const v = Number(pct);
  if (!Number.isFinite(v)) return '#64748b';
  const t = clamp(v / maxAbs, -1, 1);
  if (t >= 0) {
    const g = Math.round(120 + 80 * t);
    const r = Math.round(40 + 40 * (1 - t));
    const b = Math.round(70 + 30 * (1 - t));
    return `rgb(${r},${g},${b})`;
  }
  const intensity = Math.abs(t);
  const r = Math.round(160 + 70 * intensity);
  const g = Math.round(50 + 40 * (1 - intensity));
  const b = Math.round(60 + 30 * (1 - intensity));
  return `rgb(${r},${g},${b})`;
}

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
  }).format(n);
}

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function normalizePlpc(unrealizedPlpc, unrealizedPl, costBasis) {
  if (Number.isFinite(unrealizedPlpc)) {
    // Alpaca returns a fraction (0.05 = 5%); values already in percent are >= 1 in magnitude typically
    if (Math.abs(unrealizedPlpc) > 0 && Math.abs(unrealizedPlpc) < 1) {
      return unrealizedPlpc * 100;
    }
    return unrealizedPlpc;
  }
  const cost = Number(costBasis) || 0;
  const upl = Number(unrealizedPl) || 0;
  if (cost > 0) return (upl / cost) * 100;
  return 0;
}

function HeatmapCell(props) {
  const { x, y, width, height, name, marketValue, plPct, children, onSelect } = props;

  // With data = [{ name: 'positions', children: [...] }], leaves are depth 2.
  // Skip parent/group nodes (those that still have children).
  const isLeaf = !children || (Array.isArray(children) && children.length === 0);
  if (!isLeaf || width < 2 || height < 2 || !name || name === 'positions') return null;

  const showDetails = width > 72 && height > 48;
  const showSymbol = width > 36 && height > 24;
  const fill = plColor(plPct);

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={Math.max(0, width - 1)}
        height={Math.max(0, height - 1)}
        rx={2}
        style={{
          fill,
          stroke: 'var(--lt-surface)',
          strokeWidth: 2,
          cursor: onSelect ? 'pointer' : 'default',
        }}
        onClick={() => onSelect?.(name)}
      />
      {showSymbol && (
        <text
          x={x + 6}
          y={y + 16}
          fill="#f8fafc"
          stroke="none"
          strokeWidth={0}
          fontSize={12}
          fontWeight={700}
          style={{ pointerEvents: 'none', paintOrder: 'normal' }}
        >
          {name}
        </text>
      )}
      {showDetails && (
        <>
          <text
            x={x + 6}
            y={y + 32}
            fill="rgba(248,250,252,0.9)"
            stroke="none"
            strokeWidth={0}
            fontSize={11}
            style={{ pointerEvents: 'none' }}
          >
            {formatCurrency(marketValue)}
          </text>
          <text
            x={x + 6}
            y={y + 46}
            fill="rgba(248,250,252,0.85)"
            stroke="none"
            strokeWidth={0}
            fontSize={11}
            fontWeight={600}
            style={{ pointerEvents: 'none' }}
          >
            {formatPct(plPct)}
          </text>
        </>
      )}
    </g>
  );
}

function HeatmapTooltip({ active, payload, colorMode }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d || d.name === 'positions' || d.children?.length) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 shadow-lg text-xs">
      <div className="font-semibold text-gray-900 mb-1">{d.name}</div>
      <div className="text-gray-600">Value: {formatCurrency(d.marketValue)}</div>
      <div className="text-gray-600">Qty: {d.qty}</div>
      <div className={Number(d.plPct) >= 0 ? 'text-green-600' : 'text-red-600'}>
        {colorMode === 'today' ? 'Today' : 'Unrealized'}: {formatPct(d.plPct)}
      </div>
      <div className={Number(d.unrealizedPl) >= 0 ? 'text-green-600' : 'text-red-600'}>
        P/L $: {formatCurrency(d.unrealizedPl)}
      </div>
    </div>
  );
}

/**
 * Treemap heatmap of open Alpaca positions.
 * Tile size = |market value|; color = unrealized % or today's %.
 */
const PositionsHeatmap = ({ onSymbolSelect, height = 280, venue = 'alpaca', schwabAccount = '' }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [colorMode, setColorMode] = useState('total'); // total | today

  const loadPositions = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      if (venue === 'schwab') {
        const response = await schwabApi.getPositions();
        let rows = response.data || [];
        if (schwabAccount) {
          rows = rows.filter((p) => String(p.accountNumber) === String(schwabAccount));
        }
        setPositions(rows);
      } else {
        const response = await accountApi.getPositions();
        setPositions(response.data || []);
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPositions(true);
    const interval = setInterval(() => loadPositions(false), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, schwabAccount]);

  const leaves = useMemo(() => {
    return (positions || [])
      .map((p) => {
        const marketValue = Math.abs(Number(p.marketValue) || 0);
        if (marketValue <= 0) return null;

        const upl = Number(p.unrealizedPl) || 0;
        const totalPct = normalizePlpc(Number(p.unrealizedPlpc), upl, p.costBasis);

        const qty = Number(p.qty) || 0;
        const changeToday = Number(p.changeToday) || 0;
        const currentPrice = Number(p.currentPrice) || 0;
        let todayPct = 0;
        if (currentPrice > 0 && changeToday !== 0) {
          const prev = currentPrice - changeToday;
          todayPct = prev !== 0 ? (changeToday / prev) * 100 : 0;
        } else if (Number.isFinite(Number(p.unrealizedIntradayPl)) && marketValue > 0) {
          todayPct = (Number(p.unrealizedIntradayPl) / marketValue) * 100;
        }

        return {
          name: String(p.symbol || '').toUpperCase(),
          size: marketValue,
          marketValue,
          qty,
          unrealizedPl: upl,
          plPct: colorMode === 'today' ? todayPct : totalPct,
          side: p.side || 'long',
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.size - a.size);
  }, [positions, colorMode]);

  // Flat list of leaves — Recharts Treemap treats each top-level item as a tile.
  const treeData = leaves;

  const handleSelect = (symbol) => {
    if (typeof onSymbolSelect === 'function' && symbol) {
      onSymbolSelect(symbol);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faThLarge} className="text-indigo-600 text-sm" />
            Positions heatmap
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {venue === 'schwab' ? 'Schwab' : 'Alpaca'}
            </span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Size = market value · Color = {colorMode === 'today' ? "today's %" : 'unrealized P/L %'}
          </p>
        </div>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-[11px]">
          <button
            type="button"
            onClick={() => setColorMode('total')}
            className={`px-2.5 py-1 ${
              colorMode === 'total' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Total P/L
          </button>
          <button
            type="button"
            onClick={() => setColorMode('today')}
            className={`px-2.5 py-1 ${
              colorMode === 'today' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Today
          </button>
        </div>
      </div>

      {loading && positions.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
          Loading positions…
        </div>
      ) : error ? (
        <div className="flex items-center text-red-600 text-sm">
          <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
          {error}
        </div>
      ) : leaves.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-500 text-sm">
          No open positions to map.
        </div>
      ) : (
        <>
          <div style={{ width: '100%', height }}>
            <ResponsiveContainer width="100%" height="100%">
              <Treemap
                data={treeData}
                dataKey="size"
                aspectRatio={4 / 3}
                stroke="var(--lt-surface)"
                fill="#64748b"
                isAnimationActive={false}
                content={(cellProps) => <HeatmapCell {...cellProps} onSelect={handleSelect} />}
              >
                <Tooltip content={<HeatmapTooltip colorMode={colorMode} />} />
              </Treemap>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: plColor(-8) }} />
              Losers
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: plColor(0) }} />
              Flat
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: plColor(8) }} />
              Gainers
            </span>
            <span className="ml-auto text-gray-400">Click a tile to select symbol</span>
          </div>
        </>
      )}
    </div>
  );
};

export default PositionsHeatmap;
